/**
 * Enquiry → Won, as cumulative reach with the drop-off between each step.
 *
 * `reached` is "ever got at least this far", NOT "is sitting here now". The latter is
 * `PipelineAnalytics.by_stage`, and feeding a snapshot into a funnel produces drop-off numbers
 * that mean nothing. The difference matters most for enquiries that died: one rejected at
 * `Quotes Received` reads only `Rejected` today, and it is `Version` history that puts it back at
 * the step it actually reached.
 *
 * There is no Count ⇄ Value toggle. `Enquiry.total` is 0.00 on every live enquiry (verified
 * 2026-08-09), so a money funnel would be a row of zeros dressed up as a measurement.
 */
import { dropOffPct } from '../../lib/analytics/aggregate'
import type { Funnel } from '../../lib/analytics/types'
import { BoardCard } from './BoardCard'
import { widthPct } from './charts'

export function FunnelBoard({
  funnel, isLoading, error,
}: {
  funnel: Funnel | undefined
  isLoading: boolean
  error: unknown
}) {
  const stages = funnel?.stages ?? []
  const top = stages[0]?.reached ?? 0

  return (
    <BoardCard
      title="Enquiry → Won"
      subtitle="How many enquiries ever reached each stage, and what share was lost at each step."
      isLoading={isLoading}
      error={error}
      isEmpty={stages.length === 0 || top === 0}
    >
      <div style={{ marginTop: 12 }}>
        {stages.map((s, i) => {
          const drop = i > 0 ? dropOffPct(stages[i - 1].reached, s.reached) : null
          const isLast = i === stages.length - 1
          return (
            <div key={s.stage}>
              {drop !== null && drop > 0 && (
                <div
                  style={{
                    fontSize: 10.5, color: 'var(--danger)', margin: '1px 0 1px 190px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  −{Math.round(drop * 100)}%
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '3px 0' }}>
                <span style={{ flex: '0 0 180px', fontSize: 12.5 }}>{s.stage}</span>
                <span style={{ flex: 1, height: 20, borderRadius: 6, background: 'var(--line)' }}>
                  <span
                    style={{
                      display: 'block', height: '100%', borderRadius: 6,
                      width: `${widthPct(s.reached, top)}%`,
                      background: isLast ? 'var(--ok)' : 'var(--brand)',
                    }}
                  />
                </span>
                <span
                  className="mono"
                  style={{ flex: '0 0 108px', textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
                >
                  {s.reached}
                  <span style={{ color: 'var(--muted)' }}> · {s.lines} lines</span>
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </BoardCard>
  )
}
